import {v4 as uuid} from 'uuid';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {AspectRatio} from '@contentlab/types/aspectRatio';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {FeatureId} from '@/views/content-studio/constants';
import {AssetsQuerySorting} from '@/hooks/content-studio/use-assets-query/useAssetsQuery';

export type FileObject = {
  file: File;
  id: string | null;
  status: FileStatus;
  progress: number;
  error: string | undefined;
  metadata: {
    duration_millis: number;
    width: number;
    height: number;
  };
};

export enum FileStatus {
  IN_PROGRESS = 'in_progress',
  SUCCESS = 'success',
  ERROR = 'error',
}

type FileUpdateProps = {
  name: string;
  status?: FileObject['status'];
  error?: FileObject['error'];
  progress?: FileObject['progress'];
  id?: FileObject['id'];
};
export type SelectedAsset = {
  id: string;
  isUploading: boolean;
  thumbnail?: string;
  fileName?: string;
  type: 'Uploads' | 'Results';
  url?: string;
  durationMillis: number;
  aspectRatio?: AspectRatio;
  width?: number;
  height?: number;
};

export enum FilterOptions {
  FORMAT = 'format',
  LANGUAGE = 'language',
  SOURCE = 'source',
  PROJECT = 'project',
}

export type FilterOptionsState = {
  format: string[];
  language: string[];
  source: string[];
  project: string[];
};

export type Asset = {
  id: string;
  thumbnailUrl: string;
};

export enum ErrorKind {
  VALIDATION = 'validation',
  FAILURE = 'failure',
}

type Error = {
  title: string;
  description: string;
  id: string;
  kind: ErrorKind;
};

type RephraseProject = {
  id: string;
  currentSrt: SrtItem[];
  projectName: string;
  assetId: string;
  videoUrl: string;
  videoName: string;
};

type ContentStudioState = {
  files: Record<string, FileObject>;
  isUploading: boolean;
  selectedAssets: SelectedAsset[];
  uploadsSorting: AssetsQuerySorting;
  resultsSorting: AssetsQuerySorting;
  uploadsFilters: FilterOptionsState;
  resultsFilters: FilterOptionsState;
  errors: Error[];
  currentRephraseProject?: RephraseProject;
  isProjectShelfOpen: boolean;
  doAndDontModalFeature: string;
  doAndDontModalFeatureSeen: {[key: FeatureId]: boolean};
  isOnboardingModalOpen: boolean;
};

const initialState: ContentStudioState = {
  files: {},
  isUploading: false,
  selectedAssets: [],
  uploadsSorting: {field: 'created_at', desc: true},
  resultsSorting: {field: 'created_at', desc: true},
  uploadsFilters: {format: [], language: [], source: [], project: []},
  resultsFilters: {format: [], language: [], source: [], project: []},
  errors: [],
  isProjectShelfOpen: false,
  doAndDontModalFeature: '',
  doAndDontModalFeatureSeen: {},
  isOnboardingModalOpen: false,
};

const contentStudioStore = create<ContentStudioState>()(
  devtools(immer(() => initialState))
);

export const contentStudioActions = {
  setSelectedAssets: (assets: SelectedAsset[]) =>
    contentStudioStore.setState((state) => {
      state.currentRephraseProject = undefined;
      state.selectedAssets = assets;
    }),
  setUploadsSorting: (sorting: AssetsQuerySorting) =>
    contentStudioStore.setState((state) => {
      state.uploadsSorting = sorting;
    }),
  setResultsSorting: (sorting: AssetsQuerySorting) =>
    contentStudioStore.setState((state) => {
      state.resultsSorting = sorting;
    }),
  addFiles: (newFiles: FileObject[]) =>
    contentStudioStore.setState((state) => {
      newFiles.forEach((file) => {
        state.files[file.file.name] = file;
      });
    }),
  addSelectedAssets: (newAssets: SelectedAsset[]) =>
    contentStudioStore.setState((state) => {
      state.currentRephraseProject = undefined;
      state.selectedAssets.push(...newAssets);
    }),
  updateSelectedAsset: (asset: Partial<SelectedAsset> & {id: string}) =>
    contentStudioStore.setState((state) => {
      const index = state.selectedAssets.findIndex((a) => a.id === asset.id);
      if (index !== -1) {
        state.selectedAssets[index] = {
          ...state.selectedAssets[index],
          ...asset,
        };
      }
    }),
  updateFile: ({name, ...updates}: FileUpdateProps) =>
    contentStudioStore.setState((state) => {
      if (state.files[name]) {
        state.files[name] = {...state.files[name], ...updates};
      }
    }),
  removeFile: (fileName: string) =>
    contentStudioStore.setState((state) => {
      delete state.files[fileName];
    }),
  removeFiles: (fileNames: string[]) =>
    contentStudioStore.setState((state) => {
      fileNames.forEach((fileName) => {
        delete state.files[fileName];
      });
    }),
  handleFilter: (
    filter: keyof FilterOptionsState,
    value: string,
    type: 'uploads' | 'results'
  ) =>
    contentStudioStore.setState((state) => {
      const targetFilters =
        type === 'uploads' ? state.uploadsFilters : state.resultsFilters;
      const index = targetFilters[filter].indexOf(value);
      if (index === -1) {
        targetFilters[filter].push(value);
      } else {
        targetFilters[filter].splice(index, 1);
      }
    }),
  setProjectFilters: (project: string | undefined) =>
    contentStudioStore.setState((state) => {
      state.resultsFilters.project = project ? [project] : [];
    }),
  addError: ({
    title,
    description,
    kind,
  }: {
    title: string;
    description: string;
    kind: ErrorKind;
  }) =>
    contentStudioStore.setState((state) => {
      state.errors.push({title, description, id: uuid(), kind});
    }),
  removeError: (id: string) =>
    contentStudioStore.setState((state) => {
      state.errors = state.errors.filter((error) => error.id !== id);
    }),
  clearErrors: () =>
    contentStudioStore.setState((state) => {
      state.errors = [];
    }),
  setCurrentRephraseProject: (project: RephraseProject) =>
    contentStudioStore.setState((state) => {
      state.currentRephraseProject = project;
    }),
  clearCurrentRephraseProject: () =>
    contentStudioStore.setState((state) => {
      state.currentRephraseProject = undefined;
    }),
  updateCurrentRephraseProjectSrt: (srt: SrtItem[]) =>
    contentStudioStore.setState((state) => {
      if (state.currentRephraseProject) {
        state.currentRephraseProject.currentSrt = srt;
      }
    }),
  updateCurrentRephraseProjectName: (name: string) =>
    contentStudioStore.setState((state) => {
      if (state.currentRephraseProject) {
        state.currentRephraseProject.projectName = name;
      }
    }),
  setIsProjectShelfOpen: (isOpen: boolean) =>
    contentStudioStore.setState((state) => {
      state.isProjectShelfOpen = isOpen;
    }),
  setDoAndDontModalFeature: (feature: FeatureId | '') =>
    contentStudioStore.setState((state) => {
      state.doAndDontModalFeature = feature;
    }),
  setDoAndDontModalFeatureSeen: (feature: FeatureId) =>
    contentStudioStore.setState((state) => {
      state.doAndDontModalFeatureSeen[feature] = true;
    }),
  setIsOnboardingModalOpen: (isOpen: boolean) =>
    contentStudioStore.setState((state) => {
      state.isOnboardingModalOpen = isOpen;
    }),
};

export default contentStudioStore;
